import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, Trash2, Calendar, CreditCard, Info, AlertTriangle, Settings, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { notificationsApi } from '../lib/api';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';

const ICONS = {
  reservation: <Calendar className="w-5 h-5 text-blue-500" />,
  payment: <CreditCard className="w-5 h-5 text-green-500" />,
  alert: <AlertTriangle className="w-5 h-5 text-red-500" />,
  system: <Settings className="w-5 h-5 text-gray-500" />,
  info: <Info className="w-5 h-5 text-primary" />,
};

const PATH_MAPPINGS = {
  '/reservations': '/company/punctual',
  '/abonnements': '/company/requests',
  '/clients': '/company/clients',
  '/planning': '/company/planning',
  '/interventions': '/company/interventions',
  '/collaborateurs': '/company/collaborators',
  '/services': '/company/services',
  '/avis': '/company/reviews',
};

const NotificationsPage = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | unread | read

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await notificationsApi.getAll();
      setNotifications(response.data || []);
    } catch {
      toast.error('Erreur lors du chargement des notifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchNotifications(); }, []);

  const markAsRead = async (id) => {
    try {
      await notificationsApi.markAsRead(id);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    } catch {
      toast.error('Erreur');
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationsApi.markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      toast.success('Toutes les notifications marquées comme lues');
    } catch {
      toast.error('Erreur');
    }
  };

  const deleteOne = async (id) => {
    try {
      await notificationsApi.delete(id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    } catch {
      toast.error('Erreur');
    }
  };

  const deleteAll = async () => {
    if (!window.confirm('Supprimer toutes vos notifications ? Cette action est irréversible.')) return;
    try {
      const r = await notificationsApi.deleteAll();
      setNotifications([]);
      toast.success(`${r.data?.deleted_count ?? 0} notification(s) supprimée(s)`);
    } catch {
      toast.error('Erreur');
    }
  };

  const handleClick = (n) => {
    if (!n.is_read) markAsRead(n.id);
    if (!n.link) return;
    let target = n.link;
    const path = window.location.pathname;
    const isCompany = path === '/company' || path.startsWith('/company/');
    if (target.startsWith('/') && isCompany && !target.startsWith('/company')) {
      target = PATH_MAPPINGS[target] || `/company${target}`;
    }
    navigate(target);
  };

  const filtered = notifications.filter((n) => {
    if (filter === 'unread') return !n.is_read;
    if (filter === 'read') return n.is_read;
    return true;
  });

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Retour
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Notifications</h1>
            <p className="text-muted-foreground text-sm">
              {notifications.length} au total · {unreadCount} non lue{unreadCount > 1 ? 's' : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <Button variant="outline" size="sm" onClick={markAllAsRead} data-testid="mark-all-read">
              <CheckCheck className="w-4 h-4 mr-1.5" />
              Tout marquer comme lu
            </Button>
          )}
          {notifications.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={deleteAll}
              className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
              data-testid="delete-all"
            >
              <Trash2 className="w-4 h-4 mr-1.5" />
              Tout supprimer
            </Button>
          )}
        </div>
      </div>

      <Tabs value={filter} onValueChange={setFilter} className="w-full">
        <TabsList>
          <TabsTrigger value="all" data-testid="filter-all">
            Toutes ({notifications.length})
          </TabsTrigger>
          <TabsTrigger value="unread" data-testid="filter-unread">
            Non lues ({unreadCount})
          </TabsTrigger>
          <TabsTrigger value="read" data-testid="filter-read">
            Lues ({notifications.length - unreadCount})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center">
          <Bell className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-50" />
          <p className="text-muted-foreground">
            {filter === 'unread' ? 'Aucune notification non lue' : 'Aucune notification'}
          </p>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((n) => (
            <Card
              key={n.id}
              onClick={() => handleClick(n)}
              className={`p-4 transition-all cursor-pointer hover:shadow-md ${
                !n.is_read ? 'bg-primary/5 border-primary/20' : ''
              }`}
              data-testid={`notif-row-${n.id}`}
            >
              <div className="flex gap-3">
                <div className="flex-shrink-0 mt-0.5">{ICONS[n.type] || ICONS.info}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className={`text-sm ${!n.is_read ? 'font-semibold' : 'font-medium'}`}>
                      {n.title}
                    </p>
                    {!n.is_read && (
                      <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-1.5" />
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{n.message}</p>
                  <p className="text-xs text-muted-foreground mt-2">
                    {(() => {
                      try {
                        return formatDistanceToNow(new Date(n.created_at), {
                          addSuffix: true,
                          locale: fr,
                        });
                      } catch {
                        return '';
                      }
                    })()}
                  </p>
                </div>
                <div className="flex-shrink-0 flex items-start gap-1">
                  {!n.is_read && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        markAsRead(n.id);
                      }}
                      className="p-1.5 hover:bg-muted rounded text-muted-foreground hover:text-foreground"
                      title="Marquer comme lu"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteOne(n.id);
                    }}
                    className="p-1.5 hover:bg-red-100 rounded text-muted-foreground hover:text-red-500"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;
